// eslint-disable-next-line import/no-cycle
import { LanguageService } from '../language';
import { UserType } from '../../types/user-type';
import { Country } from '../../types/country';
import { Locale } from '../../types/locale';
import { Domain } from '../../types/domain';
import { Homepage } from '../../types/homepage';
import { DomainService } from '../domain';

export interface GlobalConfigProps {
  baseDomain: string;
  brightcoveAccountId: string;
  brightcovePlayerId: string;
  cludoCustomerID: string;
  cludoEngineId: string | string[];
  cognitoUserpoolClientId: string;
  cognitoUserpoolId: string;
  country: Country;
  locale: Locale;
  countryLocaleCode: string;
  pageId: string;
  recaptchaKey: string;
  translationUrl: string;
  twitterHandle: string;
  whiteListedDomains: string;
  storyblokAccessToken: string;
  storyblokAssetsAccessToken: string;
  emApplicationToken: string;
  userType: UserType;
  isArticle: boolean;
  windowOrigin: string;
  componentLibraryUrl: string;
  domain: Domain;
  isRebranded: boolean; // todo: remove when all are rebranded
  homepages: Record<string, Homepage>;
}

type HTMLElementContent = string | { toString: () => string };

export const DomService = {
  createElement(tagName: string, content: HTMLElementContent = '', props: Record<string, string> = {}): HTMLElement {
    const element = document.createElement(tagName);
    element.innerHTML = typeof content === 'string' ? content : content.toString();
    Object.keys(props).forEach((prop) => {
      element.setAttribute(prop, props[prop]);
    });
    return element;
  },

  getGlobalConfig(params?: {
    pageId?: string;
    locale?: string;
    country?: string;
    countryLocaleCode?: string;
    isArticle?: boolean;
    websiteUrl?: string;
    userType?: UserType;
    homepages?: Record<string, Homepage>;
  }): GlobalConfigProps {
    const { pageId, locale, country, countryLocaleCode, isArticle, websiteUrl, userType, homepages } = params || {};

    const parsedLocale: Locale = locale === 'default' ? (LanguageService.defaultLocale as Locale) : (locale as Locale);
    const parsedCountry: Country = (country === 'default' ? LanguageService.defaultCountry : country) as Country;
    const domain = DomainService.getCurrentDomain();

    return {
      baseDomain: process.env.GATSBY_BASE_DOMAIN,
      brightcoveAccountId: process.env.GATSBY_BRIGHTCOVE_ACCOUNT_ID,
      brightcovePlayerId: process.env.GATSBY_BRIGHTCOVE_PLAYER_ID,
      cludoCustomerID: process.env.GATSBY_CLUDO_CUSTOMER_ID,
      cludoEngineId:
        parsedLocale &&
        process.env.GATSBY_CLUDO_ENGINE_ID_LIST.replace(/[\s]/g, '')
          .split(',')
          .filter((item) => item.indexOf(`${parsedLocale}:`) === 0)
          .join()
          .substring(parsedLocale.length + 1, 8),
      cognitoUserpoolClientId: process.env.GATSBY_COGNITO_USERPOOL_CLIENT_ID,
      cognitoUserpoolId: process.env.GATSBY_COGNITO_USERPOOL_ID,
      // todo: replace with correct country code
      country: parsedCountry,
      locale: parsedLocale,
      countryLocaleCode,
      pageId: pageId ? `storyblok:${process.env.GATSBY_STORYBLOK_SPACE_API_KEY_NAME}:${pageId}` : undefined,
      recaptchaKey: process.env.GATSBY_GOOGLE_RECAPTCHA_KEY,
      translationUrl: `${process.env.GATSBY_COMPONENTS_LIBRARY_URL}/rcm-component-library/assets/translations/${parsedLocale}.json`,
      twitterHandle: process.env.GATSBY_TWITTER_HANDLE,
      whiteListedDomains: process.env.GATSBY_WHITE_LISTED_DOMAINS,
      storyblokAccessToken: process.env.GATSBY_STORYBLOK_SPACE_API_KEY,
      storyblokAssetsAccessToken: process.env.GATSBY_STORYBLOK_ASSETS_API_KEY,
      emApplicationToken: DomainService.getDomainSpecificEmApplicationToken(),
      isArticle,
      windowOrigin: websiteUrl,
      componentLibraryUrl: process.env.GATSBY_COMPONENTS_LIBRARY_URL,
      domain,
      userType,
      homepages,
      isRebranded: DomainService.isRebranded(parsedCountry),
    };
  },
};
